import React from 'react';
import InfoIcon from '@mui/icons-material/Info';
import { FormikProps, FormikTouched, FormikErrors } from 'formik';
import CustomButton from 'src/core/ui/CustomButton';

export function FormInfoButton<FormValues = any>({ formikProps, title = 'Show what’s required', }: FormInfoButtonProps<FormValues>) {
  const handleClick = async () => {
    // 1. Run full Yup/Formik validation so errors are populated
    const errors = (await formikProps.validateForm()) as FormikErrors<FormValues>;

    // 2. Mark *all* fields as touched based on current values
    const allTouched = markAllTouched(formikProps.values) as FormikTouched<FormValues>;

    // 3. Merge with existing touched (so we don't lose anything)
    const mergedTouched = { ...(formikProps.touched as any), ...(allTouched as any), };

    // 4. Set touched WITHOUT re-triggering validation (we already have errors)
    formikProps.setTouched(mergedTouched, false);

    // 5. Toggle global "show all tooltips as helper text" mode
    const prevStatus: any = formikProps.status || {};
    formikProps.setStatus({ ...prevStatus, showAllTooltips: !prevStatus.showAllTooltips, });
  };

  return (
    <CustomButton iconButton title={title} icon={<InfoIcon />} onClick={handleClick} />
  );
}

/**
 * Recursively mark every leaf in an object as true.
 */
function markAllTouched(obj: any): any {
  if (obj === null || obj === undefined) {
    return true;
  }
  if (Array.isArray(obj)) {
    return obj.map((v) => markAllTouched(v));
  }
  if (typeof obj === 'object') {
    return Object.keys(obj).reduce((acc: any, key) => { acc[key] = markAllTouched(obj[key]); return acc; }, {});
  }

  // primitive
  return true;
}

type FormInfoButtonProps<FormValues = any> = {
  formikProps: Pick<FormikProps<FormValues>, 'values' | 'status' | 'touched' | 'validateForm' | 'setTouched' | 'setStatus'>;
  title?: string;
};
